/**
 * DropdownElement - Custom Web Component
 * Accessible dropdown with keyboard navigation and ARIA support
 */

class DropdownElement extends HTMLElement {
  constructor() {
    super();
    this._isOpen = false;
    this.trigger = null;
    this.dropdown = null;
    this.options = [];
    this.selectedValue = null;
    this._focusedIndex = -1;
    this._instanceId = `dropdown-${Math.random().toString(36).slice(2, 9)}`;
  }

  connectedCallback() {
    this.trigger = this.querySelector('[slot="trigger"]');
    this.dropdown = this.querySelector('.dropdown');
    this.options = this.querySelectorAll('.dropdown__option');
    this.overlay = this.querySelector('.dropdown__overlay');
    this.closeButton = this.querySelector('[data-close-button]');

    if (!this.trigger || !this.dropdown) {
      console.error('Dropdown: Missing trigger or dropdown element');
      return;
    }

    this._sortSizeOptions();
    this._assignOptionIds();
    this._setupAria();
    this.setupEventListeners();
    this.initializeSelection();
    this._observeOptionsContainer();
  }

  disconnectedCallback() {
    this.removeEventListeners();
    if (this._optionsMutationObserver) {
      this._optionsMutationObserver.disconnect();
      this._optionsMutationObserver = null;
    }
  }

  /**
   * Watch for DOM mutations to the options container so that morph-based
   * section re-renders (which reuse the existing element without triggering
   * connectedCallback) still re-sort and re-initialise the options.
   */
  _observeOptionsContainer() {
    const optionsContainer = this.querySelector('.dropdown__options');
    if (!optionsContainer) return;

    this._optionsMutationObserver = new MutationObserver(() => {
      // Disconnect while we mutate the DOM ourselves to avoid infinite loops.
      this._optionsMutationObserver.disconnect();

      this.removeEventListeners();
      this.options = Array.from(this.querySelectorAll('.dropdown__option'));
      this._sortSizeOptions();
      this._assignOptionIds();
      this._setupAria();
      this.setupEventListeners();
      this.initializeSelection();

      // Resume observing after our own mutations settle.
      this._optionsMutationObserver.observe(optionsContainer, { childList: true });
    });

    this._optionsMutationObserver.observe(optionsContainer, { childList: true });
  }

  /**
   * Assign unique IDs to each option for aria-activedescendant referencing.
   */
  /**
   * Sort size dropdown options numerically (00, 0, 2, 4, 6, ...).
   */
  _sortSizeOptions() {
    if (!this.classList.contains('dropdown-size')) return;

    const optionsContainer = this.querySelector('.dropdown__options');
    if (!optionsContainer) return;

    const optionEls = Array.from(optionsContainer.querySelectorAll('.dropdown__option'));
    if (optionEls.length < 2) return;

    optionEls.sort((a, b) => {
      const aVal = a.dataset.value;
      const bVal = b.dataset.value;
      // Treat "00" as -1 so it sorts before "0"
      const aNum = aVal === '00' ? -1 : parseFloat(aVal);
      const bNum = bVal === '00' ? -1 : parseFloat(bVal);
      if (isNaN(aNum) && isNaN(bNum)) return 0;
      if (isNaN(aNum)) return 1;
      if (isNaN(bNum)) return -1;
      return aNum - bNum;
    });

    optionEls.forEach(el => optionsContainer.appendChild(el));
    // Re-query options after reordering
    this.options = this.querySelectorAll('.dropdown__option');
  }

  _assignOptionIds() {
    this.options.forEach((option, i) => {
      if (!option.id) {
        option.id = `${this._instanceId}-option-${i}`;
      }
    });
  }

  /**
   * Set up initial ARIA attributes on trigger and listbox.
   */
  _setupAria() {
    // Trigger ARIA
    this.trigger.setAttribute('aria-haspopup', 'listbox');
    if (!this.trigger.hasAttribute('aria-expanded')) {
      this.trigger.setAttribute('aria-expanded', 'false');
    }

    // Listbox ARIA
    if (!this.dropdown.hasAttribute('role')) {
      this.dropdown.setAttribute('role', 'listbox');
    }

    // Option ARIA
    this.options.forEach(option => {
      if (!option.hasAttribute('role')) {
        option.setAttribute('role', 'option');
      }
      option.setAttribute('tabindex', '-1');
    });
  }

  setupEventListeners() {
    if (!this.trigger) return;

    // Toggle dropdown on trigger click
    this._handleTriggerClick = this.handleTriggerClick.bind(this);
    this.trigger.addEventListener('click', this._handleTriggerClick);

    // Handle option selection
    this._handleOptionClickBound = this.handleOptionClick.bind(this);
    this.options.forEach(option => {
      option.addEventListener('click', this._handleOptionClickBound);
    });

    // Hide dropdown when clicking close button
    if (this.closeButton) {
      this._handleCloseClick = (e) => {
        e.stopPropagation();
        this.hide();
      };
      this.closeButton.addEventListener('click', this._handleCloseClick);
    }

    // Hide dropdown when clicking overlay
    if (this.overlay) {
      this._handleOverlayClick = this.handleOverlayClick.bind(this);
      this.overlay.addEventListener('click', this._handleOverlayClick);
    }

    // Hide dropdown when clicking outside
    this.documentClickHandler = this.handleDocumentClick.bind(this);
    document.addEventListener('click', this.documentClickHandler);

    // Handle keyboard navigation
    this.keydownHandler = this.handleKeydown.bind(this);
    this.addEventListener('keydown', this.keydownHandler);
  }

  removeEventListeners() {
    if (this.documentClickHandler) {
      document.removeEventListener('click', this.documentClickHandler);
    }
    if (this._handleTriggerClick && this.trigger) {
      this.trigger.removeEventListener('click', this._handleTriggerClick);
    }
    if (this._handleOptionClickBound) {
      this.options.forEach(option => {
        option.removeEventListener('click', this._handleOptionClickBound);
      });
    }
    if (this._handleCloseClick && this.closeButton) {
      this.closeButton.removeEventListener('click', this._handleCloseClick);
    }
    if (this._handleOverlayClick && this.overlay) {
      this.overlay.removeEventListener('click', this._handleOverlayClick);
    }
    if (this.keydownHandler) {
      this.removeEventListener('keydown', this.keydownHandler);
    }
  }

  initializeSelection() {
    // Find initially selected option
    const selectedOption = this.querySelector('.dropdown__option--selected');
    if (selectedOption) {
      this.selectedValue = selectedOption.dataset.value;
      this.updateTriggerText(selectedOption);
    }
  }

  handleTriggerClick(e) {
    e.stopPropagation();
    this.toggle();
  }

  handleOptionClick(e) {
    const option = e.currentTarget;
    const value = option.dataset.value;

    this.selectOption(value);
    this.hide();
  }

  handleOverlayClick(e) {
    e.stopPropagation();
    this.hide();
  }

  handleDocumentClick(e) {
    if (this.isOpen && !this.contains(e.target)) {
      this.hide();
    }
  }

  handleKeydown(e) {
    // When closed, Enter/Space/ArrowDown/ArrowUp should open the dropdown
    if (!this.isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (document.activeElement === this.trigger || this.trigger.contains(document.activeElement)) {
          e.preventDefault();
          this.show();
        }
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        this.hide();
        break;

      case 'ArrowDown':
        e.preventDefault();
        this.navigateOptions(1);
        break;

      case 'ArrowUp':
        e.preventDefault();
        this.navigateOptions(-1);
        break;

      case 'Home':
        e.preventDefault();
        this._focusOptionByIndex(0);
        break;

      case 'End':
        e.preventDefault();
        this._focusOptionByIndex(this.options.length - 1);
        break;

      case 'Enter':
      case ' ':
        e.preventDefault();
        if (this._focusedIndex >= 0 && this._focusedIndex < this.options.length) {
          this.selectOption(this.options[this._focusedIndex].dataset.value);
          this.hide();
        }
        break;

      case 'Tab':
        // Close the dropdown on Tab and let focus move naturally
        this.hide();
        break;
    }
  }

  navigateOptions(direction) {
    let newIndex = this._focusedIndex + direction;

    if (newIndex < 0) newIndex = this.options.length - 1;
    if (newIndex >= this.options.length) newIndex = 0;

    this._focusOptionByIndex(newIndex);
  }

  /**
   * Focus an option by its index and update visual + ARIA state.
   */
  _focusOptionByIndex(index) {
    if (index < 0 || index >= this.options.length) return;

    // Remove focus from all options
    this.options.forEach(opt => opt.classList.remove('dropdown__option--focused'));

    // Add focus to new option
    this._focusedIndex = index;
    const option = this.options[index];
    option.classList.add('dropdown__option--focused');

    // Update aria-activedescendant on the trigger
    this.trigger.setAttribute('aria-activedescendant', option.id);

    // Scroll the focused option into view within the scrollable container
    option.scrollIntoView({ block: 'nearest' });
  }

  selectOption(value) {
    // Remove selection from all options
    this.options.forEach(opt => {
      opt.classList.remove('dropdown__option--selected');
      opt.removeAttribute('aria-selected');
    });

    // Find and select the new option
    const selectedOption = this.querySelector(`[data-value="${value}"]`);
    if (selectedOption) {
      selectedOption.classList.add('dropdown__option--selected');
      selectedOption.setAttribute('aria-selected', 'true');
      this.selectedValue = value;
      this.updateTriggerText(selectedOption);

      // Update hidden input if exists (for form compatibility)
      const hiddenInput = this.querySelector('input[type="checkbox"]');
      if (hiddenInput) {
        hiddenInput.value = value;

        hiddenInput.setAttribute('data-connected-product-url', selectedOption.dataset.connectedProductUrl);
        hiddenInput.setAttribute('data-option-value-id', selectedOption.dataset.optionValueId);
        hiddenInput.setAttribute('data-option-available', selectedOption.dataset.optionAvailable);
        hiddenInput.setAttribute('data-variant-id', selectedOption.dataset.variantId);

        // Trigger change event on hidden input for form handling
        const changeEvent = new Event('change', { bubbles: true });
        hiddenInput.dispatchEvent(changeEvent);
      }

      // Dispatch custom event
      this.dispatchEvent(new CustomEvent('dropdown:change', {
        detail: {
          value: value,
          option: selectedOption,
          dropdown: this
        },
        bubbles: true
      }));
    }
  }

  updateTriggerText(option) {
    const triggerText = this.trigger.querySelector('.dropdown-trigger__text');
    if (triggerText) {
      const optionText = option.querySelector('.dropdown__option-text').innerHTML;

      triggerText.innerHTML = optionText;
    }
  }

  show() {
    if (!this.dropdown || !this.trigger) {
      console.error('Dropdown: Cannot show - missing elements');
      return;
    }

    const allDropdowns = document.querySelectorAll('dropdown-element');
    allDropdowns.forEach(dropdown => {
      if (dropdown instanceof DropdownElement && dropdown !== this && dropdown.isOpen) {
        dropdown.hide();
      }
    });

    this.isOpen = true;
    this.dropdown.classList.remove('popup--hidden');
    this.dropdown.style.opacity = '1';
    this.dropdown.style.visibility = 'visible';
    this.dropdown.setAttribute('aria-hidden', 'false');
    this.trigger.setAttribute('aria-expanded', 'true');

    // Focus the selected option, or the first option
    const selectedIndex = Array.from(this.options).findIndex(
      opt => opt.classList.contains('dropdown__option--selected')
    );
    this._focusOptionByIndex(selectedIndex >= 0 ? selectedIndex : 0);

    // Keep focus on the trigger so keyboard events route through it
    this.trigger.focus();

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('dropdown:show', {
      detail: { dropdown: this }
    }));
  }

  hide() {
    if (!this.dropdown || !this.trigger) return;

    this.isOpen = false;
    this.dropdown.classList.add('popup--hidden');
    this.dropdown.style.opacity = '0';
    this.dropdown.style.visibility = 'hidden';
    this.dropdown.setAttribute('aria-hidden', 'true');
    this.trigger.setAttribute('aria-expanded', 'false');

    // Clear focused state
    this._focusedIndex = -1;
    this.options.forEach(opt => opt.classList.remove('dropdown__option--focused'));
    this.trigger.removeAttribute('aria-activedescendant');

    // Return focus to trigger
    this.trigger.focus();

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('dropdown:hide', {
      detail: { dropdown: this }
    }));
  }

  toggle() {
    if (this.isOpen) {
      this.hide();
    } else {
      this.show();
    }
  }

  // Public API methods
  open() {
    this.show();
  }

  close() {
    this.hide();
  }

  getValue() {
    return this.selectedValue;
  }

  setValue(value) {
    this.selectOption(value);
  }

  get isOpen() {
    return this._isOpen;
  }

  set isOpen(value) {
    this._isOpen = Boolean(value);
  }
}

// Register the custom element
customElements.define('dropdown-element', DropdownElement);

// Export for module usage if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = DropdownElement;
}
